import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTestById } from '../../actions/testActions';
import { FaClock, FaStar, FaListOl, FaChalkboardTeacher, FaCheck } from 'react-icons/fa';
import { MdCategory, MdOutlineQuiz } from 'react-icons/md';
import { Spinner } from 'react-bootstrap';

const ViewTest = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { testbyid, fetchingtestbyid, error } = useSelector(state => state.tests);

  useEffect(() => {
    if (!testbyid || testbyid._id !== id) {
      dispatch(fetchTestById(id));
    }
  }, [id, dispatch, testbyid]);

  if (fetchingtestbyid) return (
    <div className="d-flex justify-content-center align-items-center" style={{ height: '30vh', color: '#0096c7' }}>
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </div>
  );

  if (error) return (
    <div className="alert alert-danger text-center mt-4">
      Error loading test: {error}
    </div>
  );

  if (!testbyid) return null;

  const isAnswerCorrect = (questionIndex, optionIndex) => {
    const correctAnswer = testbyid.correctAnswers[questionIndex];
    if (Array.isArray(correctAnswer)) {
      return correctAnswer.includes(optionIndex);
    }
    return correctAnswer === optionIndex;
  };

  return (
    <div className="container-fluid py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10">
          <div className="card shadow-sm">
            <div className="card-header">
              <h3 className="mb-0">Test Details</h3>
            </div>
            <div className="card-body">
              {/* Test Metadata */}
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-body">
                      <h4 className="card-title">{testbyid.testName}</h4>
                      <div className="d-flex align-items-center mb-2">
                        <MdCategory className="me-2 text-secondary" />
                        <span className="badge bg-info">{testbyid.category}</span>
                      </div>
                      {testbyid.testSeries && (
                        <div className="d-flex align-items-center mb-2">
                          <MdOutlineQuiz className="me-2 text-secondary" />
                          <span className="badge bg-warning text-dark">{testbyid.testSeries}</span>
                        </div>
                      )}
                      <div className="d-flex align-items-center mb-2">
                        <FaChalkboardTeacher className="me-2 text-secondary" />
                        <span>Created by: {testbyid.teacherName}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex flex-column h-100 justify-content-between">
                        <div className="d-flex align-items-center mb-3">
                          <FaClock className="me-2 text-secondary" />
                          <div>
                            <h6 className="mb-0">Duration</h6>
                            <p className="mb-0 fw-bold">{testbyid.totalMinutes} minutes</p>
                          </div>
                        </div>
                        <div className="d-flex align-items-center mb-3">
                          <FaStar className="me-2 text-secondary" />
                          <div>
                            <h6 className="mb-0">Total Marks</h6>
                            <p className="mb-0 fw-bold">{testbyid.totalMarks}</p>
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          <FaListOl className="me-2 text-secondary" />
                          <div>
                            <h6 className="mb-0">Questions</h6>
                            <p className="mb-0 fw-bold">{testbyid?.questions?.length}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Questions Section */}
              <div className="mt-4">
                <h4 className="mb-3 border-bottom pb-2">Questions</h4>
                {testbyid?.questions?.map((question, qIndex) => (
                  <div key={qIndex} className="card mb-4 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <h5 className="card-title">
                          <span className="badge bg-info me-2">{question.questionNumber || qIndex + 1}</span>
                          <span dangerouslySetInnerHTML={{ __html: question.questionText }} />
                        </h5>
                        <span className="badge bg-secondary">
                          {question.type === 'multiple' ? 'Multiple Correct' : 'Single Correct'}
                        </span>
                      </div>

                      <div className="row g-2">
                        {question?.options?.map((option, optionIndex) => (
                          <div className="col-12 col-sm-6" key={optionIndex}>
                            <div 
                              className={`p-3 rounded ${isAnswerCorrect(qIndex, optionIndex) ? 
                                'bg-success bg-opacity-10 border border-success' : 
                                'bg-light'}`}
                            >
                              <div className="d-flex align-items-center">
                                <span className="fw-bold me-2">{String.fromCharCode(65 + optionIndex)}.</span>
                                <span dangerouslySetInnerHTML={{ __html: option }} />
                                {isAnswerCorrect(qIndex, optionIndex) && (
                                  <span className="ms-2 text-success">
                                    <FaCheck />
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {question.explanation && (
                        <div className="mt-3 p-3 bg-light rounded">
                          <h6 className="text-primary">Explanation:</h6>
                          <p className="mb-0">{question.explanation}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewTest;